import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, updateIdField, updateItem} from "./utils";

const initialState = {
    items: [],
    paymentHistory: [],
    duePayment: [],
    vendorUploadedDoc: [],
    token: null,
    isValid: null,
    fetchStatus: "idle",
    fetchByIdStatus: "idle",
    fetchDuePaymentStatus: "idle",
    createStatus: "idle",
    updateStatus: "idle",
    resetDataStatus: "idle",
    updateVendorPaymentStatus: "idle",
    fetchPaymentHistoryStatus: "idle",
    fetchVendorDocStatus: "idle",
    loginStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    documentUploadStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchVendorStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    vendorLoginStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    deleteStatus: "idle",
    pagination: {},
    docHistoryPagination: {},
    error: null,
    loginError: null,
    docUploadError: null,
    documentFetchError: null,
};

//Vendor login
export const vendorLogin = createAsyncThunk('auth/vendorLogin', async (data) => {
    try {
        const response = await api.post('/auth/vendorlogin', data, {
            vendorToken: 'vendorAuthToken',
        });
        return {...response}
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

//Fetch vendors
export const fetchVendors = createAsyncThunk('vendor/fetchVendors', async ({
                                                                               roleSign,
                                                                               scopeId,
                                                                               page = 1,
                                                                               limit = 10
                                                                           } = {}) => {
    try {
        const response = await api.get(`/${roleSign}/vendors?id=${scopeId}&page=${page}&limit=${limit}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

//Create vendors
export const createVendor = createAsyncThunk('vendor/createVendor', async ({roleSign, data}) => {
    try {
        const response = await api.post(`/${roleSign}/vendors`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

//Fetch vendor by Id
export const fetchVendorById = createAsyncThunk('vendor/fetchVendorById', async ({roleSign, id}) => {
    try {
        const response = await api.get(`/${roleSign}/vendors/${id}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

//Update vendor
export const updateVendor = createAsyncThunk('vendor/updateVendor', async ({roleSign, data, id}) => {
    try {
        const response = await api.patch(`/${roleSign}/vendors/${id}`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

//Fetch due payments
export const fetchDuePayments = createAsyncThunk('vendor/fetchDuePayments', async ({roleSign, scopeId, id}) => {
    try {
        const response = await api.get(`/${roleSign}/vendors/get-payment/${id}?id=${scopeId}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

//Update vendor payment individually
export const updateVendorPayment = createAsyncThunk('vendor/updateVendorPayment', async ({
                                                                                             roleSign,
                                                                                             scopeId,
                                                                                             data,
                                                                                             id
                                                                                         }) => {
    try {
        const response = await api.patch(`/${roleSign}/vendors/clear-payment/${id}?id=${scopeId}`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

//Update vendor partial payment
export const updateVendorPartialPayment = createAsyncThunk('vendor/updateVendorPartialPayment', async ({
                                                                                                           roleSign,
                                                                                                           scopeId,
                                                                                                           data,
                                                                                                           id
                                                                                                       }) => {
    try {
        const response = await api.patch(`/${roleSign}/vendors/partial-payment/${id}`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

//Fetch vendor payment history
export const fetchPaymentHistory = createAsyncThunk('vendor/fetchPaymentHistory', async ({
                                                                                             roleSign,
                                                                                             scopeId,
                                                                                             id,
                                                                                             page,
                                                                                             limit
                                                                                         }) => {
    try {
        let response;
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);

        const queryString = params.toString();
        const fullParams = queryString ? `&${queryString}` : '';
        response = await api.get(`/${roleSign}/vendors/paymentHistory/${id}?id=${scopeId}${fullParams}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

//document upload by vendor
export const documentUpload = createAsyncThunk('vendor/documentUpload', async (data) => {
    try {
        const response = await api.post(`/uploadDocument`, data, {
            vendorToken: 'vendorAuthToken',// overrides default token
        });
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

//Fetch vendor's document history
export const fetchVendorDocument = createAsyncThunk('vendor/fetchVendorDocument', async ({
                                                                                             scopeId,
                                                                                             vendorId
                                                                                         }) => {
    try {
        const response = await api.get(`history?id=${scopeId}&vendor=${vendorId}`, {
            vendorToken: 'vendorAuthToken',// overrides default token
        });
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

//Reset vendor data
export const resetVendorPaymentData = createAsyncThunk('vendor/resetVendorPaymentData', async ({roleSign, id}) => {
    try {
        const response = await api.delete(`${roleSign}/vendors/resetData/${id}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

//Delete vendor
export const deleteVendor = createAsyncThunk('vendor/deleteVendor', async ({roleSign, id}) => {
    try {
        const response = await api.delete(`${roleSign}/vendors/${id}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});


const vendorSlice = createSlice({
    name: "vendor",
    initialState,
    reducers: {
        fetchVendorToken: (state) => {
            const token = localStorage.getItem('vendorAuthToken');
            state.token = token;
            state.isValid = token != null;
        },
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
        resetResetDataStatus(state) {
            state.resetDataStatus = 'idle';
        },
        resetUpdateVendorPaymentStatus(state) {
            state.updateVendorPaymentStatus = 'idle';
        },
        resetDocumentUploadStatus(state) {
            state.documentUploadStatus = 'idle';
        }
    },
    extraReducers: (builder) => {
        builder

            // vendor login
            .addCase(vendorLogin.pending, (state) => {
                state.vendorLoginStatus = 'loading';
            })
            .addCase(vendorLogin.fulfilled, (state, action) => {
                if (action.payload.status === 200) {
                    state.vendorLoginStatus = 'success';
                    state.isValid = true;
                    state.token = action.payload.data.vendorToken;
                    localStorage.setItem("vendorAuthToken", action.payload.data?.vendorToken || null);
                } else {
                    state.vendorLoginStatus = 'failed';
                    state.loginError = action.payload.data.message;
                }
            })
            .addCase(vendorLogin.rejected, (state, action) => {
                state.vendorLoginStatus = 'failed';
                state.loginError = action.error.message;
            })

            //Fetch vendors
            .addCase(fetchVendors.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchVendors.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                const {data, ...pagination} = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchVendors.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })

            //Fetch vendors by Id
            .addCase(fetchVendorById.pending, (state) => {
                state.fetchByIdStatus = 'loading';
            })
            .addCase(fetchVendorById.fulfilled, (state, action) => {
                const {fetchByIdStatus, error} = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = [];
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchVendorById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })

            //Create vendor
            .addCase(createVendor.pending, (state) => {
                state.createStatus = 'loading';
            })
            .addCase(createVendor.fulfilled, (state, action) => {
                const {createStatus, error} = handleResponseStatus(action.payload, 'createStatus');
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createVendor.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })

            //upload document by vendor
            .addCase(documentUpload.pending, (state) => {
                state.documentUploadStatus = 'loading';
            })
            .addCase(documentUpload.fulfilled, (state, action) => {
                const {documentUploadStatus, error} = handleResponseStatus(action.payload, 'documentUploadStatus');
                state.documentUploadStatus = documentUploadStatus;
                state.docUploadError = error;
            })
            .addCase(documentUpload.rejected, (state, action) => {
                state.documentUploadStatus = 'failed';
                state.docUploadError = action.error.message;
            })

            //Fetch vendor's document history
            .addCase(fetchVendorDocument.pending, (state) => {
                state.fetchVendorDocStatus = 'loading';
            })
            .addCase(fetchVendorDocument.fulfilled, (state, action) => {
                const {fetchVendorDocStatus, error} = handleResponseStatus(action.payload, 'fetchVendorDocStatus');
                state.vendorUploadedDoc = [];
                const {data, ...pagination} = action.payload.data;
                state.vendorUploadedDoc = fetchVendorDocStatus === "success" ? updateIdField(data, state.vendorUploadedDoc) : state.vendorUploadedDoc;
                state.fetchVendorDocStatus = fetchVendorDocStatus;
                state.docHistoryPagination = pagination;
                state.documentFetchError = error;
            })
            .addCase(fetchVendorDocument.rejected, (state, action) => {
                state.fetchVendorDocStatus = 'failed';
                state.documentFetchError = action.error.message;
            })

            //Update vendor
            .addCase(updateVendor.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateVendor.fulfilled, (state, action) => {
                const {updateStatus, error} = handleResponseStatus(action.payload, 'updateStatus');
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updateVendor.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })

            //Update vendor payment
            .addCase(updateVendorPayment.pending, (state) => {
                state.updateVendorPaymentStatus = 'loading';
            })
            .addCase(updateVendorPayment.fulfilled, (state, action) => {
                const {
                    updateVendorPaymentStatus,
                    error
                } = handleResponseStatus(action.payload, 'updateVendorPaymentStatus');
                state.updateVendorPaymentStatus = updateVendorPaymentStatus;
                state.error = error;
            })
            .addCase(updateVendorPayment.rejected, (state, action) => {
                state.updateVendorPaymentStatus = 'failed';
                state.error = action.error.message;
            })

            //Update vendor partial payment
            .addCase(updateVendorPartialPayment.pending, (state) => {
                state.updateVendorPaymentStatus = 'loading';
            })
            .addCase(updateVendorPartialPayment.fulfilled, (state, action) => {
                const {
                    updateVendorPaymentStatus,
                    error
                } = handleResponseStatus(action.payload, 'updateVendorPaymentStatus');
                state.updateVendorPaymentStatus = updateVendorPaymentStatus;
                state.error = error;
            })
            .addCase(updateVendorPartialPayment.rejected, (state, action) => {
                state.updateVendorPaymentStatus = 'failed';
                state.error = action.error.message;
            })

            //Fetch vendor due payment
            .addCase(fetchDuePayments.pending, (state) => {
                state.fetchDuePaymentStatus = 'loading';
            })
            .addCase(fetchDuePayments.fulfilled, (state, action) => {
                const {fetchDuePaymentStatus, error} = handleResponseStatus(action.payload, 'fetchDuePaymentStatus');
                const {data} = action.payload.data;
                state.duePayment = fetchDuePaymentStatus === "success" ? data : state.duePayment;
                state.fetchDuePaymentStatus = fetchDuePaymentStatus;
                state.error = error;
            })
            .addCase(fetchDuePayments.rejected, (state, action) => {
                state.fetchDuePaymentStatus = 'failed';
                state.error = action.error.message;
            })

            //Delete vendor
            .addCase(deleteVendor.pending, (state) => {
                state.deleteStatus = 'loading';
            })
            .addCase(deleteVendor.fulfilled, (state, action) => {
                const {deleteStatus, error} = handleResponseStatus(action.payload, 'deleteStatus');
                const id = action.meta.arg.id;
                if (deleteStatus === "success") {
                    state.items = state.items.filter(item => item.id !== id);
                }
                state.deleteStatus = deleteStatus;
                state.error = error;
            })
            .addCase(deleteVendor.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.error.message;
            })

            //Reset vendor data
            .addCase(resetVendorPaymentData.pending, (state) => {
                state.resetDataStatus = 'loading';
            })
            .addCase(resetVendorPaymentData.fulfilled, (state, action) => {
                const {resetDataStatus, error} = handleResponseStatus(action.payload, 'resetDataStatus');
                state.resetDataStatus = resetDataStatus;
                state.error = error;
            })
            .addCase(resetVendorPaymentData.rejected, (state, action) => {
                state.resetDataStatus = 'failed';
                state.error = action.error.message;
            })

            //Fetch payment history
            .addCase(fetchPaymentHistory.pending, (state) => {
                state.fetchPaymentHistoryStatus = 'loading';
            })
            .addCase(fetchPaymentHistory.fulfilled, (state, action) => {
                const {
                    fetchPaymentHistoryStatus,
                    error
                } = handleResponseStatus(action.payload, 'fetchPaymentHistoryStatus');
                const {data, ...pagination} = action.payload.data;
                state.paymentHistory = fetchPaymentHistoryStatus === "success" ? updateIdField(data) : state.paymentHistory;
                state.fetchPaymentHistoryStatus = fetchPaymentHistoryStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchPaymentHistory.rejected, (state, action) => {
                state.fetchPaymentHistoryStatus = 'failed';
                state.error = action.error.message;
            });
    }
});

export const {
    resetUpdateStatus,
    resetUpdateVendorPaymentStatus,
    resetDeleteStatus,
    resetCreateStatus,
    resetDocumentUploadStatus,
    fetchVendorToken,
    resetResetDataStatus
} = vendorSlice.actions;
export default vendorSlice.reducer;